import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

/**
 * A full-height column that shrinks when the soft keyboard opens.
 *
 * THE KEYBOARD IS THE PLATFORM CONSTRAINT OF M6c, and this component is the whole of the app's
 * answer to it. M6a's standing defect 4 is "on a phone the Check / Next controls can sit under the
 * keyboard" — a bottom-anchored flex spacer meeting a soft keyboard — and both M6c screens dock
 * strictly more than the screen with that defect: the journal a correction card AND a suggestion
 * strip, the chat three reply chips AND a composer.
 *
 * `100dvh` alone is not enough. On Android Chrome the dynamic viewport tracks the LAYOUT viewport,
 * which does not shrink when the keyboard opens; `visualViewport` does, and its `resize`/`scroll`
 * pair is the only thing in the platform that reports the keyboard at all.
 *
 * `offsetTop` is in the sum because iOS scrolls the visual viewport UP rather than shrinking it
 * when a field near the bottom is focused. Without that term the docked strip goes off the top
 * instead of off the bottom, which is the same bug wearing a different hat.
 *
 * ONE COPY, DELIBERATELY. It shipped as two — private in `screens/Journal.tsx` and lifted into
 * `components/chat/` — for about an hour, which is exactly the shape of M6a's dictation tint (one
 * function copied into two files, drifted by one code point, killed the feature silently). Two
 * implementations of the one thing standing between this milestone and an unusable phone screen is
 * not a risk worth carrying for the convenience of a local definition.
 *
 * UNVERIFIED, and it cannot be verified by anything in this repo: the web suite has no DOM, and a
 * desktop browser's device mode does not simulate a soft keyboard. This needs a real phone, and the
 * definition of done says so.
 */
export function KeyboardBox({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  const [covered, setCovered] = useState(0);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    // No visualViewport (an old browser, a test, SSR) simply means no adjustment: the column falls
    // back to `100dvh` and behaves exactly as it did before this component existed.
    if (!vv) return;
    const apply = () => {
      setCovered(Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)));
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
    };
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--nn-bg)',
        ...style,
        // Last, and not overridable by `style`: a caller that set its own height would be opting
        // out of the only thing this component does.
        height: `calc(100dvh - var(--app-shell-footer-offset, 0rem) - ${covered}px)`,
      }}
    >
      {children}
    </div>
  );
}
